import { EventEmitter, Injectable } from '@angular/core';
import { AuthService } from '@gelato-api-ui/core/auth/auth.service';
import { HelpCenterArticles } from '@gelato-api-ui/core/help-center/help-center-articles';
import { KeycloakLocaleCode, LocaleCode } from '@gelato-api-ui/core/i18n/locales.constant';
import { FeatureSwitcherService } from '@gelato-api-ui/sdk/src/lib/feature-switcher/feature-switcher.service';
import { FeatureFlagEnum } from '@gelato-api-ui/sdk/src/lib/feature-switcher/featureFlagEnum';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeaturesService } from '../../shared/services/features.service';
import { SidebarItem } from './sidebar-item';
import { SidebarNavItemIdEnum } from './sidebar-nav-item-id.enum';

@Injectable({
  providedIn: 'root',
})
export class NavigationTreeService {
  toggleMobileSidebarTrigger = new EventEmitter<void>();

  private readonly billingTree = [
    new SidebarItem({
      id: SidebarNavItemIdEnum.BILLING_ENTITIES,
      name: 'txt_sidebar_billing',
      iconSvg: 'billing',
      children: [
        new SidebarItem({
          id: SidebarNavItemIdEnum.BILLING_ENTITIES,
          name: 'txt_sidebar_billing_entities',
          route: ['/billing-entities'],
          isVisible: () => this.authService.hasReadPermission('finance', 'billing-entity'),
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.PRICE_PLANS,
          name: 'txt_sidebar_price_plans',
          route: ['/price-plans'],
          isVisible: () => this.authService.hasReadPermission('finance', 'price-plan'),
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.PRICE_PLAN_SUBSCRIPTIONS,
          name: 'txt_sidebar_price_plan_subscriptions',
          route: ['/price-plan-subscriptions'],
          isVisible: () => this.authService.hasReadPermission('finance', 'price-plan-subscription'),
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.PAYMENT_TRANSACTIONS,
          name: 'txt_sidebar_payment_transactions',
          route: ['/payment-transactions'],
          isVisible: () =>
            combineLatest([
              this.authService.isGelatoUser(),
              this.authService.hasReadPermission('finance', 'price-plan'),
            ]).pipe(map(([isGelatoUser, hasPermission]) => isGelatoUser && hasPermission)),
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.BANK_ACCOUNT,
          name: 'txt_sidebar_bank_account',
          route: ['/bank-account'],
          isVisible: () =>
            combineLatest([
              this.authService.isGelatoUser(),
              this.authService.hasReadPermission('finance', 'price-plan'),
            ]).pipe(map(([isGelatoUser, hasPermission]) => isGelatoUser && hasPermission)),
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.PREPAYMENTS,
          name: 'txt_sidebar_prepayments',
          route: ['/prepayments'],
          isVisible: () =>
            combineLatest([
              this.authService.isGelatoUser(),
              this.authService.hasReadPermission('finance', 'prepayment'),
            ]).pipe(map(([isGelatoUser, hasPermission]) => isGelatoUser && hasPermission)),
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.PRICE_CACHE_MANAGEMENT,
          name: 'txt_sidebar_cache',
          route: ['/price-cache-management'],
          isVisible: () => this.authService.hasReadPermission('finance', 'price-plan-subscription'),
        }),
      ],
    }),
  ];

  private readonly settingsTree = [
    new SidebarItem({
      id: SidebarNavItemIdEnum.SETTINGS,
      name: 'txt_sidebar_settings',
      iconSvg: 'settings',
      children: [
        new SidebarItem({
          id: SidebarNavItemIdEnum.PRODUCTION_CONFIGURATIONS,
          name: 'txt_sidebar_production_configurations',
          route: ['/production-configurations'],
          isVisible: () => this.authService.hasReadPermission('production', 'configuration'),
        }),
      ],
    }),
  ];

  private readonly mockupStudioAdminTree = [
    new SidebarItem({
      id: SidebarNavItemIdEnum.MOCKUP_STUDIO,
      name: 'txt_sidebar_mockup_studio',
      iconSvg: 'mockup_gallery',
      isVisible: () => this.authService.isGelatoUser(),
      children: [
        new SidebarItem({
          id: SidebarNavItemIdEnum.MOCKUP_STUDIO_CUSTOMER_TEMPLATES,
          name: 'txt_sidebar_mockup_studio_customer_templates',
          route: ['/mockup-studio/customer-templates'],
          isVisible: () => this.authService.hasReadPermission('mockup', 'client-scene'),
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.MOCKUP_STUDIO_GELATO_TEMPLATES,
          name: 'txt_sidebar_mockup_studio_gelato_templates',
          route: ['/mockup-studio/gelato-templates'],
          isVisible: () => this.authService.hasReadPermission('mockup', 'public-scene'),
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.MOCKUP_STUDIO_TEMPLATES_APPROVAL,
          name: 'txt_sidebar_mockup_studio_templates_approval',
          route: ['/mockup-studio/templates-approval'],
        }),
      ],
    }),
  ];

  private readonly developerTree = [
    new SidebarItem({
      id: SidebarNavItemIdEnum.DEVELOPER,
      name: 'txt_sidebar_developer',
      iconSvg: 'developer',
      children: [
        new SidebarItem({
          id: SidebarNavItemIdEnum.API_DOCUMENTATION,
          name: 'txt_navigation_api_documentation',
          href: 'https://dashboard.gelato.com/docs/',
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.SETTINGS_API_KEYS,
          name: 'txt_sidebar_api_keys',
          route: ['/keys'],
          isVisible: () => this.authService.hasReadPermission('identity', 'key'),
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.SETTINGS_NOTIFICATIONS,
          name: 'txt_sidebar_webhooks',
          route: ['/webhooks'],
          isVisible: () => this.authService.hasReadPermission('notification', 'subscription'),
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.AUDIT_LOGS,
          name: 'txt_sidebar_audit_logs',
          route: ['/audit-logs'],
          isVisible: () =>
            combineLatest([this.authService.isGelatoUser(), this.authService.hasReadPermission('ecommerce', '*')]).pipe(
              map(([isGelatoUser, hasPermission]) => isGelatoUser && hasPermission),
            ),
        }),
      ],
    }),
  ];

  private readonly brandingTree = [
    new SidebarItem({
      id: SidebarNavItemIdEnum.BRANDING,
      name: 'txt_sidebar_branding',
      iconSvg: 'branding',
      isVisible: () =>
        combineLatest([
          this.featureSwitcherService.isFeatureEnabledForCurrentUser(FeatureFlagEnum.branded_packaging),
          this.authService.isGelatoUser(),
          this.authService.hasWritePermission('order', 'configuration'),
        ]).pipe(
          map(([isFeatureEnabled, isGelatoUser, hasPermission]) => isFeatureEnabled && !isGelatoUser && hasPermission),
        ),
      children: [
        new SidebarItem({
          id: SidebarNavItemIdEnum.BRANDING_CONFIGURE,
          name: 'txt_sidebar_your_configure',
          route: ['/branding/configure'],
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.BRANDING_DESIGNS,
          name: 'txt_sidebar_your_projects',
          route: ['/branding/projects'],
        }),
      ],
    }),
  ];

  private readonly priceNavigatorTree = [
    new SidebarItem({
      id: SidebarNavItemIdEnum.PRICE_NAVIGATOR,
      name: 'txt_sidebar_price_navigator',
      iconSvg: 'price-navigator',
      isVisible: () =>
        combineLatest([
          this.featureSwitcherService.isFeatureEnabledForCurrentUser(FeatureFlagEnum.price_navigator),
          this.authService.isGelatoUser(),
        ]).pipe(
          map(([isPriceNavigatorDashboardEnabled, isGelatoUser]) => isPriceNavigatorDashboardEnabled && !isGelatoUser),
        ),
      children: [
        new SidebarItem({
          id: SidebarNavItemIdEnum.PRICE_NAVIGATOR_DASHBOARD,
          name: 'txt_sidebar_pn_dashboard',
          route: ['/price-navigator/dashboard'],
          analyticsKey: 'Viewed Price Navigator Dashboard',
          analyticsProperties: { referer: 'mainMenu' },
          analyticsEventVersion: 2,
          isVisible: () =>
            this.featureSwitcherService.isFeatureEnabledForCurrentUser(FeatureFlagEnum.price_navigator_strategies),
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.PRICE_NAVIGATOR_DASHBOARD,
          name: 'txt_sidebar_pn_product_prices',
          route: ['/price-navigator/prices'],
          analyticsKey: 'Viewed Price Navigator Product Prices',
          analyticsProperties: { referer: 'mainMenu' },
          analyticsEventVersion: 2,
          isVisible: () => this.featureSwitcherService.isFeatureEnabledForCurrentUser(FeatureFlagEnum.price_navigator),
        }),
        new SidebarItem({
          id: SidebarNavItemIdEnum.PRICE_NAVIGATOR_STRATEGIES,
          name: 'txt_sidebar_pn_pricing_strategies',
          route: ['/price-navigator/strategies'],
          isVisible: () =>
            combineLatest([
              this.featureSwitcherService.isFeatureEnabledForCurrentUser(FeatureFlagEnum.price_navigator_strategies),
              this.authService.isGelatoUser(),
            ]).pipe(map(([isFeatureEnabled, isGelatoUser]) => isFeatureEnabled && !isGelatoUser)),
        }),
      ],
    }),
  ];

  readonly tree: SidebarItem[] = [
    new SidebarItem({
      id: SidebarNavItemIdEnum.HOME,
      name: 'txt_sidebar_home',
      iconSvg: 'home',
      route: ['/home'],
      isVisible: () => this.authService.hasReadPermission('order', 'analytics'),
    }),
    new SidebarItem({
      id: SidebarNavItemIdEnum.PRODUCT_CATALOGUE,
      name: 'txt_sidebar_product_catalogue',
      iconSvg: 'product-catalog',
      route: ['/catalogue'],
      isVisible: () => this.authService.hasReadPermission('product', 'catalogue'),
      analyticsKey: 'catalogueNavigationStep',
    }),
    new SidebarItem({
      id: SidebarNavItemIdEnum.CLIENTS,
      name: 'txt_sidebar_clients',
      iconSvg: 'building',
      route: ['/clients'],
      isVisible: () => this.authService.hasReadPermission('identity', 'client'),
    }),
    new SidebarItem({
      id: SidebarNavItemIdEnum.USERS,
      name: 'txt_sidebar_users',
      iconSvg: 'user',
      route: ['/users'],
      isVisible: () =>
        combineLatest([this.authService.hasReadPermission('identity', 'user'), this.authService.isGelatoUser()]).pipe(
          map(([hasPermission, isGelatoUser]) => hasPermission && isGelatoUser),
        ),
    }),
    new SidebarItem({
      id: SidebarNavItemIdEnum.ORDERS,
      name: 'txt_sidebar_orders',
      iconSvg: 'basket',
      route: ['/orders'],
      isVisible: () => this.authService.hasReadPermission('order', 'order'),
    }),
    new SidebarItem({
      id: SidebarNavItemIdEnum.E_COMMERCE_STORES,
      name: 'txt_sidebar_stores',
      iconSvg: 'stores',
      route: ['/stores'],
      isVisible: () => this.authService.hasReadPermission('ecommerce', '*'),
    }),
    null,
    new SidebarItem({
      id: SidebarNavItemIdEnum.APPS_PERSONALISATION_STUDIO,
      name: 'txt_sidebar_personalisation_studio',
      iconSvg: 'personalisation',
      route: ['/app/personalization-studio'],
      analyticsKey: 'Personalization Studio Dashboard Viewed',
      analyticsProperties: { referer: 'mainMenu' },
      analyticsEventVersion: 2,
      isVisible: () => this.featureSwitcherService.isFeatureEnabledForCurrentUser(FeatureFlagEnum.personalisation_page),
    }),
    new SidebarItem({
      id: SidebarNavItemIdEnum.E_COMMERCE_PRODUCT_TEMPLATES,
      name: 'txt_sidebar_templates',
      iconSvg: 'templates',
      route: ['/templates'],
      isVisible: () =>
        combineLatest([this.authService.isGelatoUser(), this.authService.hasReadPermission('ecommerce', '*')]).pipe(
          map(([isGelatoUser, hasPermission]): boolean => !isGelatoUser && hasPermission),
        ),
    }),
    ...this.brandingTree,
    new SidebarItem({
      id: SidebarNavItemIdEnum.ANALYTICS,
      name: 'txt_sidebar_analytics',
      iconSvg: 'analytics',
      route: ['/analytics'],
      isVisible: () => this.authService.hasReadPermission('order', 'analytics'),
    }),
    ...this.priceNavigatorTree,

    new SidebarItem({
      id: SidebarNavItemIdEnum.MOCKUP_STUDIO,
      name: 'txt_sidebar_mockup_studio',
      iconSvg: 'mockup_gallery',
      route: ['/mockup-studio'],
      isVisible: () =>
        combineLatest([
          this.featureSwitcherService.isFeatureEnabledForCurrentUser(FeatureFlagEnum.mockup_studio),
          this.authService.isGelatoUser(),
          this.authService.hasWritePermission('mockup', 'client-scene'),
        ]).pipe(
          map(
            ([isFeatureEnabled, isGelatoUser, hasWritePermission]) =>
              isFeatureEnabled && !isGelatoUser && hasWritePermission,
          ),
        ),
    }),

    ...this.mockupStudioAdminTree,

    // ---- BILLING TREE ----
    ...this.billingTree,
    ...this.settingsTree,
    ...this.developerTree,

    new SidebarItem({
      id: SidebarNavItemIdEnum.FEATURES,
      name: 'txt_sidebar_features',
      iconSvg: 'puzzle',
      route: ['/features'],
      isVisible: () => of(this.featureService.isEnabled(this.featureService.FEATURE_MANAGEMENT)),
    }),

    null, // null values are used as separators
    new SidebarItem({
      id: SidebarNavItemIdEnum.RESOURCE_CENTER,
      name: 'txt_recource_center',
      iconSvg: 'resource-center',
      href: 'https://www.gelato.com/en-US/resource-center',
    }),
    new SidebarItem({
      id: SidebarNavItemIdEnum.SUPPORT,
      name: 'txt_navigation_customer_support',
      iconSvg: 'headset_mic',
      href: this.getHelpCenterUrl(),
    }),
  ];

  constructor(
    private readonly featureService: FeaturesService,
    private readonly featureSwitcherService: FeatureSwitcherService,
    private readonly authService: AuthService,
    private readonly translateService: TranslateService,
  ) {}

  private getHelpCenterUrl(): string {
    const localeMap = {
      [LocaleCode.EN]: KeycloakLocaleCode.EN,
      [LocaleCode.FR]: KeycloakLocaleCode.FR,
      [LocaleCode.ES]: KeycloakLocaleCode.ES,
      [LocaleCode.IT]: KeycloakLocaleCode.IT,
      [LocaleCode.DE]: KeycloakLocaleCode.DE,
      [LocaleCode.SE]: KeycloakLocaleCode.SE,
      [LocaleCode.NO]: KeycloakLocaleCode.NO,
      [LocaleCode.JP]: KeycloakLocaleCode.JP,
      [LocaleCode.VI]: KeycloakLocaleCode.VI,
      [LocaleCode.NL]: KeycloakLocaleCode.NL,
      [LocaleCode.DA]: KeycloakLocaleCode.DA,
    };

    const currentLang = this.translateService.currentLang;
    const locale = localeMap[currentLang] || KeycloakLocaleCode.EN;

    return `${HelpCenterArticles.helpCenter}/${locale}`;
  }
}
